import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useSucursal } from '../../utils/sucursalContext';
import AdminLayout from '../../components/layout/AdminLayout';

type Reserva = {
  id: string;
  usuario: string;
  email: string;
  clase: string;
  fecha: string;
  horario: string;
  hora: string;
  estado: string;
  fechaReserva: string;
};

type Feriado = {
  id: string;
  fecha: string;
  nombre: string;
  tipo: string;
};

export default function ReservasPage() {
  const { sucursalActiva } = useSucursal();
  const [reservas, setReservas] = useState<Reserva[]>([]);
  const [feriados, setFeriados] = useState<Feriado[]>([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroFecha, setFiltroFecha] = useState('todas');

  useEffect(() => {
    cargarTodo();
  }, [sucursalActiva?.id]);

  const cargarTodo = async () => {
    if (!sucursalActiva?.id) {
      setReservas([]);
      setFeriados([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const [{ data: horarios, error: horariosError }, { data: feriadosData, error: feriadosError }] = await Promise.all([
        supabase
          .from('horarios')
          .select('id, nombre_clase, hora_inicio, hora_fin')
          .eq('sucursal_id', sucursalActiva.id),
        supabase
          .from('feriados')
          .select('id, fecha, nombre, tipo')
          .eq('sucursal_id', sucursalActiva.id)
          .eq('activo', true)
          .gte('fecha', new Date().toISOString().split('T')[0])
          .order('fecha', { ascending: true }),
      ]);

      if (horariosError) throw horariosError;
      if (feriadosError) throw feriadosError;

      const horarioMap = new Map((horarios || []).map((h: any) => [h.id, h]));
      const horarioIds = (horarios || []).map((h: any) => h.id);

      setFeriados(feriadosData || []);

      if (horarioIds.length === 0) {
        setReservas([]);
        return;
      }

      const { data: reservasRaw, error: reservasError } = await supabase
        .from('reservas')
        .select('id, fecha_reserva, hora, estado, created_at, usuario_id, horario_id')
        .in('horario_id', horarioIds)
        .order('fecha_reserva', { ascending: false });

      if (reservasError) throw reservasError;

      const usuarioIds = Array.from(new Set((reservasRaw || []).map((r: any) => r.usuario_id).filter(Boolean)));
      const { data: usuarios, error: usuariosError } = await supabase
        .from('usuarios')
        .select('id, nombre, email')
        .in('id', usuarioIds);

      if (usuariosError) throw usuariosError;

      const usuariosMap = new Map((usuarios || []).map((u: any) => [u.id, u]));

      const parsed: Reserva[] = (reservasRaw || []).map((r: any) => {
        const usuario = usuariosMap.get(r.usuario_id);
        const horario = horarioMap.get(r.horario_id);
        return {
          id: r.id,
          usuario: usuario?.nombre || 'Usuario desconocido',
          email: usuario?.email || '-',
          clase: horario?.nombre_clase || 'Clase',
          fecha: r.fecha_reserva,
          horario: horario ? `${horario.hora_inicio} - ${horario.hora_fin}` : '-',
          hora: r.hora ? String(r.hora).slice(0, 5) : '-',
          estado: r.estado,
          fechaReserva: r.created_at,
        };
      });

      setReservas(parsed);
    } catch (error) {
      console.error('Error cargando reservas:', error);
      setReservas([]);
      setFeriados([]);
    } finally {
      setLoading(false);
    }
  };

  const reservasFiltradas = useMemo(() => {
    return reservas.filter((r) => {
      const bySearch =
        r.usuario.toLowerCase().includes(busqueda.toLowerCase()) ||
        r.email.toLowerCase().includes(busqueda.toLowerCase()) ||
        r.clase.toLowerCase().includes(busqueda.toLowerCase());
      const byEstado = filtroEstado === 'todos' || r.estado.toLowerCase() === filtroEstado;
      const byFecha = filtroFecha === 'todas' || r.fecha === filtroFecha;
      return bySearch && byEstado && byFecha;
    });
  }, [reservas, busqueda, filtroEstado, filtroFecha]);

  const fechasDisponibles = useMemo(() => {
    return Array.from(new Set(reservas.map((r) => r.fecha))).sort().reverse();
  }, [reservas]);

  const stats = {
    totalReservas: reservas.length,
    confirmadas: reservas.filter((r) => r.estado === 'confirmada').length,
    pendientes: reservas.filter((r) => r.estado === 'pendiente').length,
    canceladas: reservas.filter((r) => r.estado === 'cancelada').length,
  };

  const cancelarReserva = async (id: string) => {
    if (!confirm('¿Cancelar esta reserva?')) return;

    const { error } = await supabase.rpc('cancelar_reserva', { reserva_id: id });
    if (error) {
      alert(error.message || 'No se pudo cancelar la reserva');
      return;
    }

    setReservas((prev) => prev.map((r) => (r.id === id ? { ...r, estado: 'cancelada' } : r)));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reservas</h1>
            <p className="text-sm text-gray-500 mt-1">Gestión real de reservas por sucursal</p>
          </div>
        </div>

        {feriados.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-amber-800 mb-2">Próximos días sin servicio</h4>
            <div className="flex flex-wrap gap-2">
              {feriados.slice(0, 6).map((f) => (
                <span key={f.id} className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                  {new Date(`${f.fecha}T12:00:00`).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })} - {f.nombre}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard title="Total" value={stats.totalReservas} icon="ri-bookmark-line" />
          <StatCard title="Confirmadas" value={stats.confirmadas} icon="ri-check-line" />
          <StatCard title="Pendientes" value={stats.pendientes} icon="ri-time-line" />
          <StatCard title="Canceladas" value={stats.canceladas} icon="ri-close-circle-line" />
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar usuario, email o clase"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="todos">Todos los estados</option>
            <option value="confirmada">Confirmada</option>
            <option value="pendiente">Pendiente</option>
            <option value="cancelada">Cancelada</option>
          </select>
          <select
            value={filtroFecha}
            onChange={(e) => setFiltroFecha(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="todas">Todas las fechas</option>
            {fechasDisponibles.map((fecha) => (
              <option key={fecha} value={fecha}>
                {new Date(`${fecha}T12:00:00`).toLocaleDateString('es-CL')}
              </option>
            ))}
          </select>
          <button onClick={cargarTodo} className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700">
            Actualizar
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Usuario</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Clase</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Horario</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reservasFiltradas.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                      No se encontraron reservas con los filtros aplicados
                    </td>
                  </tr>
                ) : (
                  reservasFiltradas.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{r.usuario}</p>
                          <p className="text-xs text-gray-500">{r.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{r.clase}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {new Date(`${r.fecha}T12:00:00`).toLocaleDateString('es-CL')}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{r.horario}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                            r.estado === 'confirmada'
                              ? 'bg-green-100 text-green-700'
                              : r.estado === 'pendiente'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {r.estado}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {r.estado !== 'cancelada' && (
                          <button
                            onClick={() => cancelarReserva(r.id)}
                            className="px-3 py-1.5 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
                          >
                            Cancelar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function StatCard({ title, value, icon }: { title: string; value: number; icon: string }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className="w-10 h-10 bg-teal-50 text-teal-600 rounded-lg flex items-center justify-center">
          <i className={`${icon} text-lg`}></i>
        </div>
      </div>
    </div>
  );
}
